/**
 * Choice fields a rep may mark "Heard" (BV-2742).
 *
 * Heard leaves a field empty until after the call, when its value is recovered from the
 * recording. That is harmless for a field nothing depends on, and wrong for a BRANCHING
 * field: the disposition engine reads those during the call to decide which follow-ups
 * are required, so an empty gate (coverage status, PA required, …) silently drops its
 * follow-ups from the required set and the script reads complete when it is not.
 *
 * Text, number and date fields have always been heard-eligible; choice fields never
 * were. This table opens Heard to the choice fields the engine does NOT branch on —
 * checked against `@100mslive/disposition-engine` v0.60.2 (`workflows/unifiedmedbv.ts`:
 * every field read by a `conditional_params` gate or by scenario selection is left out,
 * as are the choice fields read to decide a follow-up, such as submission methods and
 * reason for non-coverage). Add a field here only after checking it gates nothing.
 *
 * Pure (no React, no I/O).
 */

interface HeardChoiceRules {
  /** Top-level field ids. */
  topLevel: ReadonlySet<string>;
  /** Paths inside one entity element (`coverage.deductible_applies`), for any entity. */
  entity: ReadonlySet<string>;
}

const MEDBV_HEARD_CHOICES: HeardChoiceRules = {
  topLevel: new Set([
    'coverage_period_type',
    'policy_holder_relationship',
    'coordination_of_benefits',
    'deductible_included_in_out_of_pocket',
    'accumulator_plan',
    'maximizer_plan',
    'office_visit_deductible_applies',
    'office_visit_oopmax_applies',
    'office_visit_applies',
  ]),
  entity: new Set([
    'coverage.deductible_applies',
    'coverage.oopmax_applies',
    'coverage.covered_hundred_percent',
    'coverage.coverage_under_pbm',
    'prior_auth_process.prior_authorization_requirements',
    'prior_auth_process.prior_authorization_notification_methods',
    'pre_determination_process.pre_determination_requirements',
    'pre_determination_process.pre_determination_notification_methods',
    'step_therapy.step_therapy_method',
    'specialty_pharmacy_details.buy_and_bill_allowed',
    'specialty_pharmacy_details.buy_and_bill_exclusions_limitations_exists',
    'specialty_pharmacy_details.specialty_pharmacy_network_status',
    'specialty_pharmacy_details.specialty_pharmacy_type',
    'specialty_pharmacy_details.specialty_pharmacy_exclusions_exist',
    'specialty_pharmacy_details.specialty_pharmacy_exception_process_available',
    'specialty_pharmacy_details.rems_status1',
    'specialty_pharmacy_details.rems_status2',
    'specialty_pharmacy_details.rems_status3',
    'specialty_pharmacy_coverage.specialty_pharmacy_coverage',
    'specialty_pharmacy_coverage.specialty_pharmacy_coverage_status',
    'specialty_pharmacy_coverage.specialty_pharmacy_same_coverage',
    'specialty_pharmacy_coverage.specialty_pharmacy_reason_for_noncoverage',
    'specialty_pharmacy_coverage.specialty_pharmacy_covered_hundred_percent',
    'specialty_pharmacy_coverage.specialty_pharmacy_deductible_applies',
    'specialty_pharmacy_coverage.specialty_pharmacy_oopmax_applies',
  ]),
};

/**
 * workflow_type → rules. Only the workflows checked against the unifiedmedbv engine:
 * medbhobv runs on it (its `medbv_` / `bho_` entities share the relative paths). bho and
 * medbv have engine modules of their own and keep the type-only behaviour until checked.
 */
const HEARD_CHOICE_RULES: Readonly<Record<string, HeardChoiceRules>> = {
  unifiedmedbv: MEDBV_HEARD_CHOICES,
  medbhobv: MEDBV_HEARD_CHOICES,
};

/** Whether the choice field at `key` (`plan_x` or `drug[0].coverage.x`) may be marked heard. */
export function isHeardChoiceAllowed(workflowType: string | undefined, key: string): boolean {
  const rules = workflowType ? HEARD_CHOICE_RULES[workflowType] : undefined;
  if (!rules) return false;
  const entityPath = /^\w+\[\d+\]\.(.+)$/.exec(key);
  return entityPath ? rules.entity.has(entityPath[1]) : rules.topLevel.has(key);
}
